import type { FunctionComponent } from 'react';
import { Fragment } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import { Badge } from '~/ui/core/badge/Badge';

import { useOptionalUrlTiles } from '../../../url-tiles';
import { TaskClusterLinks } from '../../tasks/TaskClusterLinks';
import { useJobArchitectureUrlTileActions } from '../../url-tiles/use-job-architecture-url-tile-actions';
import {
    createAutomationAssessmentTilePayload,
    createAutomationRecommendationTilePayload,
    createAutomationStepTilePayload,
    createBusinessImpactTilePayload,
    type ProfileTaskAutomation,
} from '../automation-assessment';
import type { ProfileTask, ProfileTaskParentContext } from '../model/profile-task';
import { profileTaskRichTextToPlainText } from '../profile-task-rich-text';
import { getProfileTaskClassificationTranslationKey, profileTaskTranslationKeys } from '../profile-task-translations';
import { type TaskDetailCommentable, TaskDetailFields } from './TaskDetailFields';
import type { TaskMetaRow } from './TaskFieldPrimitives';
import { TaskMetaRowInfo } from './TaskFieldPrimitives';

export type ProfileTaskMetaRow = TaskMetaRow;

/**
 * Compact label/value metadata block shared by the task tile's view and edit bodies, so both
 * render classification and estimated effort on the same grid.
 */
export const ProfileTaskMetaRows: FunctionComponent<{ readonly rows: readonly ProfileTaskMetaRow[] }> = ({ rows }) => {
    if (rows.length === 0) {
        return null;
    }

    return (
        <dl
            className="m-0 grid grid-cols-[max-content_minmax(0,1fr)] items-center gap-x-xxl gap-y-l"
            data-testid="profile-task-meta"
        >
            {rows.map(({ key, label, tooltip, value }) => (
                <Fragment key={key}>
                    <dt className="flex items-center gap-xs text-body-s font-medium leading-normal text-text-primary">
                        {label}
                        {tooltip === undefined ? null : <TaskMetaRowInfo label={label} text={tooltip} />}
                    </dt>
                    <dd className="m-0 min-w-0">{value}</dd>
                </Fragment>
            ))}
        </dl>
    );
};

/** Blank reasoning means the value was set by hand, so the fallback copy explains that instead. */
const toReasoningTooltip = (reasoning: string | undefined, fallback: string): string => {
    const text = profileTaskRichTextToPlainText(reasoning)?.trim();

    return text === undefined || text.length === 0 ? fallback : text;
};

const useProfileTaskMetaRows = (task: ProfileTask, architectureId: string): readonly ProfileTaskMetaRow[] => {
    const translate = useTranslate();
    const manuallyEdited = translate(profileTaskTranslationKeys.manuallyEdited);
    const { maximumHoursPerWeek, minimumHoursPerWeek } = task.effort ?? {};
    const rows: ProfileTaskMetaRow[] = [];

    if (task.clusters.length > 0) {
        rows.push({
            key: 'clusters',
            label: translate(profileTaskTranslationKeys.clusters),
            value: (
                <TaskClusterLinks
                    architectureId={architectureId}
                    clusters={task.clusters}
                    testId="profile-task-clusters"
                />
            ),
        });
    }

    if (task.classification !== undefined) {
        const isKnownClassification = task.classification === 'commodity' || task.classification === 'contribution';

        rows.push({
            key: 'classification',
            label: translate(profileTaskTranslationKeys.classification),
            tooltip: toReasoningTooltip(task.classificationReasoning, manuallyEdited),
            value: (
                <Badge
                    data-testid="profile-task-classification"
                    variant={task.classification === 'contribution' ? 'success' : 'gray'}
                >
                    {translate(getProfileTaskClassificationTranslationKey(task.classification))}
                    {isKnownClassification ? null : ` · ${task.classification}`}
                </Badge>
            ),
        });
    }

    if (minimumHoursPerWeek !== undefined || maximumHoursPerWeek !== undefined) {
        rows.push({
            key: 'effort',
            label: translate(profileTaskTranslationKeys.effort),
            tooltip: toReasoningTooltip(task.effortReasoning, manuallyEdited),
            value: (
                <Badge data-testid="profile-task-effort" variant="gray">
                    {translate(profileTaskTranslationKeys.effortFormat, {
                        maximum: maximumHoursPerWeek ?? minimumHoursPerWeek,
                        minimum: minimumHoursPerWeek ?? maximumHoursPerWeek,
                    })}
                </Badge>
            ),
        });
    }

    return rows;
};

export const ProfileTaskFields: FunctionComponent<{
    readonly activeLanguage: LanguageCodeForJSA;
    readonly automation: ProfileTaskAutomation | undefined;
    readonly commentable?: TaskDetailCommentable;
    readonly context: ProfileTaskParentContext;
    readonly task: ProfileTask;
}> = ({ activeLanguage, automation, commentable, context, task }) => {
    const metaRows = useProfileTaskMetaRows(task, context.architectureId);
    const urlTiles = useOptionalUrlTiles();
    const { openSkillTile } = useJobArchitectureUrlTileActions();
    const address = { context, language: activeLanguage, taskId: task.taskId, taskVersionId: task.taskVersionId };

    return (
        <TaskDetailFields
            {...(urlTiles === null
                ? {}
                : {
                      automationActions: {
                          openAssessment: () => urlTiles.openTile(createAutomationAssessmentTilePayload(address)),
                          openBusinessImpact: () => urlTiles.openTile(createBusinessImpactTilePayload(address)),
                          openRecommendation: (automationId) =>
                              urlTiles.openTile(
                                  createAutomationRecommendationTilePayload({ ...address, automationId }),
                              ),
                          openStep: (workflowStepId) =>
                              urlTiles.openTile(createAutomationStepTilePayload({ ...address, workflowStepId })),
                      },
                  })}
            commentable={commentable}
            metaRows={metaRows}
            task={{
                automation,
                description: task.description,
                effort: task.effort,
                skills: task.requiredSkills,
                taskId: task.taskId,
                workflowSteps: task.workflowSteps,
            }}
            testIdPrefix="profile-task"
            onOpenSkill={(skill) => openSkillTile(skill.skillId, activeLanguage, skill.level)}
        />
    );
};
